import type { Helpers } from "./endpoints-shared";
import type { In, Out } from "./operations";
import type { PageRequest } from "./types";

export interface WordingFilters {
  app: "staff" | "member";
  locale: "en" | "es";
  /** "club": this club's wording over the platform's. "platform" (platform staff): only the platform layer. */
  scope?: "club" | "platform" | undefined;
  q?: string | undefined;
  module?: string | undefined;
  screen?: string | undefined;
  state?: "all" | "overridden" | "inherited" | undefined;
}

/** Editable interface wording: the apps' built-in messages, the platform's overrides and the club's own. */
export function wordingEndpoints({ get, put, send, paged }: Omit<Helpers, "http" | "post" | "patch" | "del">) {
  return {
    /** Every message with its built-in text and overrides, searchable. Needs `clubs.text`. */
    listKeys: (filters: WordingFilters & PageRequest) =>
      get<Out<"core_list_keys">>(
        "/wording/keys",
        paged(filters, {
          app: filters.app,
          locale: filters.locale,
          scope: filters.scope,
          q: filters.q,
          module: filters.module,
          screen: filters.screen,
          state: filters.state,
        }),
      ),
    /** The modules and screens messages belong to, for the filters. */
    listScreens: (app: "staff" | "member", locale: "en" | "es", signal?: AbortSignal) =>
      get<Out<"core_list_screens">>("/wording/screens", { query: { app, locale }, signal }),
    readKey: (key: string, app: "staff" | "member", locale: "en" | "es", signal?: AbortSignal) =>
      get<Out<"core_read_key">>(`/wording/keys/${encodeURIComponent(key)}`, { query: { app, locale }, signal }),
    setClubText: (input: In<"core_set_club_text">) => put<Out<"core_set_club_text">>("/wording/club", input),
    clearClubText: (key: { app: "staff" | "member"; locale: "en" | "es"; key: string }) =>
      send<Out<"core_clear_club_text">>("DELETE", "/wording/club", undefined, { query: key }),
    /** Platform staff only. */
    setPlatformText: (input: In<"core_set_platform_text">) => put<Out<"core_set_platform_text">>("/wording/platform", input),
    clearPlatformText: (key: { app: "staff" | "member"; locale: "en" | "es"; key: string }) =>
      send<Out<"core_clear_platform_text">>("DELETE", "/wording/platform", undefined, { query: key }),
    /** What the app applies over its built-in messages for this club and language. Any member of the club may ask. The browser revalidates by ETag. */
    effective: (app: "staff" | "member", locale: "en" | "es", signal?: AbortSignal) =>
      get<Out<"core_effective">>("/wording/effective", { query: { app, locale }, signal }),
  };
}
